/**
 * PostHog Analytics — SDK init & typed event tracking
 *
 * Initializes posthog-js with proxy, person_profiles, session replay privacy.
 * Exports the singleton instance + typed tracking utilities.
 *
 * Fires alongside existing GTM/dataLayer events (see analytics.ts) — does NOT replace them.
 */

import posthog from 'posthog-js';

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;

if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_POSTHOG_API_HOST,
    ui_host: import.meta.env.VITE_POSTHOG_UI_HOST,
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
    cross_subdomain_cookie: import.meta.env.VITE_POSTHOG_CROSS_SUBDOMAIN_COOKIE === 'true',
  });

  // Tag every event with environment for filtering in PostHog
  posthog.register({
    environment: import.meta.env.VITE_POSTHOG_ENVIRONMENT,
    platform: 'scio3',
  });
}

// ── Typed Event Tracking ──────────────────────────────────────────────────────

/** All PostHog event names — `object_action` snake_case convention. */
export type PostHogEventName =
  // Category 1: Authentication
  | 'user_logged_in'
  | 'user_logged_out'
  | 'user_password_reset'
  | 'mfa_enrolled'
  // Category 2: Account & Get Started
  | 'account_created'
  | 'user_signed_up'
  | 'trial_started'
  | 'get_started_step_completed'
  | 'get_started_completed'
  | 'onboarding_completed'
  | 'onboarding_submit_failed'
  // Category 3: Screen/Device Management
  | 'screen_pairing_started'
  | 'screen_paired'
  | 'screen_pairing_failed'
  | 'content_pushed'
  | 'temp_takeover_used'
  | 'screen_bulk_action'
  // Category 4: Content Management
  | 'asset_created'
  | 'asset_previewed'
  | 'playlist_created'
  | 'playlist_updated'
  | 'schedule_created'
  | 'schedule_updated'
  // Category 5: Collaboration & Teams
  | 'team_switched'
  | 'folder_created'
  | 'api_key_created'
  // Category 7: AI Features
  | 'optibot_conversation_started'
  | 'optibot_message_sent'
  | 'optibot_feedback_given'
  // Category 8: Navigation & Engagement
  | 'page_viewed'
  | 'search_performed'
  | 'bulk_action_performed'
  | 'export_generated'
  | 'notification_clicked'
  | 'upgrade_cta_clicked'
  // Category 9: Errors & System
  | 'error_encountered'
  | 'api_error_occurred'
  | 'help_accessed';

/**
 * Type-safe PostHog event capture. No-ops if PostHog key is not configured.
 *
 * Usage: `trackEvent('asset_created', { asset_type: 'image', is_first: true })`
 */
interface PostHogCapturedEvent {
  event: string;
  properties: Record<string, unknown>;
  timestamp: number;
}

interface WindowWithE2E extends Window {
  __posthogCapturedEvents?: PostHogCapturedEvent[];
}

function pushE2EEvent(event: string, properties: Record<string, unknown>) {
  const w = window as WindowWithE2E;
  (w.__posthogCapturedEvents ??= []).push({ event, properties, timestamp: Date.now() });
}

export function trackEvent(event: PostHogEventName, properties?: Record<string, unknown>) {
  if (!posthogKey) return;
  posthog.capture(event, properties);

  // In E2E mode, also push to a window array for Playwright assertion
  if (import.meta.env.VITE_E2E === 'true') {
    pushE2EEvent(event, properties ?? {});
  }
}

// In E2E mode, initialize the capture array and wrap identify/group for assertions
if (import.meta.env.VITE_E2E === 'true') {
  (window as WindowWithE2E).__posthogCapturedEvents = [];
}
if (import.meta.env.VITE_E2E === 'true' && posthogKey) {
  const origIdentify = posthog.identify.bind(posthog);
  posthog.identify = (distinctId: string, properties?: Record<string, unknown>) => {
    pushE2EEvent('$identify', { distinct_id: distinctId, ...properties });
    origIdentify(distinctId, properties);
  };

  const origGroup = posthog.group.bind(posthog);
  posthog.group = (type: string, key: string, properties?: Record<string, unknown>) => {
    pushE2EEvent('$groupidentify', { group_type: type, group_key: key, ...properties });
    origGroup(type, key, properties);
  };
}

export { posthog };
